import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize, Plus, MousePointer2,
  Trash2, Copy, Loader2, PenLine,
} from "lucide-react";

export const FIELD_TYPES = [
  ["inspection_item", "Inspection Item", "II"],
  ["contractor_signature", "Contractor Signature", "CS"],
  ["client_signature", "Client Signature", "CLS"],
  ["contractor_name", "Contractor Name", "CN"],
  ["client_name", "Client Name", "CLN"],
  ["contractor_signature_date", "Contractor Signature Date", "CSD"],
  ["client_signature_date", "Client Signature Date", "CLSD"],
  ["completion_date", "Completion Date", "CD"],
  ["status", "Status", "ST"],
  ["comment", "Comment", "CM"],
  ["evidence_reference", "Evidence Reference", "EV"],
  ["hp_decision", "HP Decision", "HPD"],
  ["ap_decision", "AP Decision", "APD"],
  ["free_text", "Free Text", "TX"],
];
const TYPE_LABEL = Object.fromEntries(FIELD_TYPES.map(([v, l]) => [v, l]));
const TYPE_ABBR = Object.fromEntries(FIELD_TYPES.map(([v, , a]) => [v, a]));

const typeColor = (t) => {
  if (t === "inspection_item") return { border: "#0f766e", bg: "rgba(15,118,110,0.10)", chip: "#0f766e" };
  if (t.includes("signature")) return { border: "#b45309", bg: "rgba(180,83,9,0.10)", chip: "#b45309" };
  if (t.includes("name") || t.includes("date")) return { border: "#0369a1", bg: "rgba(3,105,161,0.08)", chip: "#0369a1" };
  if (t.includes("decision")) return { border: "#7e22ce", bg: "rgba(126,34,206,0.08)", chip: "#7e22ce" };
  return { border: "#475569", bg: "rgba(71,85,105,0.08)", chip: "#475569" };
};

const EMPTY_ITEM = {
  item_number: "", item_description: "", requirement: "", section_name: "Mapped Items",
  point_type: "NONE", evidence_required: "", mandatory: true,
  requires_contractor_signature: false, requires_client_signature: false, requires_client_release: false,
};

export default function ITPMappingStudio({ itp, onTemplateChanged }) {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pageImg, setPageImg] = useState(null);
  const [rendering, setRendering] = useState(true);
  const [fields, setFields] = useState([]);
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState("select");
  const [drawType, setDrawType] = useState("inspection_item");
  const [draft, setDraft] = useState(null);
  const [tempBoxes, setTempBoxes] = useState({});
  const [panel, setPanel] = useState(null); // {kind:"new"|"edit", field, form, itemForm, linkExisting}
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const opRef = useRef(null);
  const drawTypeRef = useRef(drawType);
  useEffect(() => { drawTypeRef.current = drawType; }, [drawType]);

  const reloadFields = useCallback(async () => {
    const r = await api.get(`/itp-templates/${itp.id}/mapped-fields`);
    setFields(r.data.fields || []);
    setItems(r.data.items || []);
  }, [itp.id]);

  useEffect(() => { reloadFields(); }, [reloadFields]);

  // Load the rendered PDF once.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/itp-templates/${itp.id}/rendered.pdf`, { responseType: "arraybuffer" });
        const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
        try {
          pdfjs.GlobalWorkerOptions.workerSrc = (await import("pdfjs-dist/build/pdf.worker.mjs?url")).default;
        } catch {
          pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
        }
        const doc = await pdfjs.getDocument({ data: r.data }).promise;
        if (cancel) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
      } catch (e) {
        console.error("PDF load failed", e);
        toast.error("Unable to load the document preview.");
      }
    })();
    return () => { cancel = true; };
  }, [itp.id]);

  // Render the current page at fit-width × zoom.
  useEffect(() => {
    if (!pdfDoc) return;
    let cancel = false;
    (async () => {
      setRendering(true);
      try {
        const page = await pdfDoc.getPage(pageNum);
        const vp1 = page.getViewport({ scale: 1 });
        const cw = (containerRef.current?.clientWidth || 900) - 2;
        const scale = Math.min((cw / vp1.width) * zoom, 4);
        const vp = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width; canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        if (cancel) return;
        setPageImg({ url: canvas.toDataURL("image/png"), w: vp.width, h: vp.height });
      } finally {
        if (!cancel) setRendering(false);
      }
    })();
    return () => { cancel = true; };
  }, [pdfDoc, pageNum, zoom]);

  const pageFields = useMemo(() => fields.filter(f => f.page_number === pageNum), [fields, pageNum]);
  const itemById = useMemo(() => Object.fromEntries(items.map(i => [i.id, i])), [items]);

  const normFromEvent = (e) => {
    const rect = wrapperRef.current.getBoundingClientRect();
    return {
      x: Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1),
      y: Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1),
    };
  };

  // ---------- drawing a new box ----------
  const onWrapperMouseDown = (e) => {
    if (mode !== "draw" || e.button !== 0) return;
    e.preventDefault();
    const p = normFromEvent(e);
    opRef.current = { type: "draw", start: p };
    setDraft({ x: p.x, y: p.y, width: 0, height: 0 });
  };

  useEffect(() => {
    const onMove = (e) => {
      const op = opRef.current;
      if (!op || !wrapperRef.current) return;
      const p = normFromEvent(e);
      if (op.type === "draw") {
        setDraft({
          x: Math.min(op.start.x, p.x), y: Math.min(op.start.y, p.y),
          width: Math.abs(p.x - op.start.x), height: Math.abs(p.y - op.start.y),
        });
      } else if (op.type === "move") {
        const dx = p.x - op.start.x, dy = p.y - op.start.y;
        const b = op.orig;
        setTempBoxes(tb => ({ ...tb, [op.fieldId]: {
          x: Math.min(Math.max(b.x + dx, 0), 1 - b.width), y: Math.min(Math.max(b.y + dy, 0), 1 - b.height),
          width: b.width, height: b.height } }));
      } else if (op.type === "resize") {
        const b = op.orig;
        setTempBoxes(tb => ({ ...tb, [op.fieldId]: {
          x: b.x, y: b.y,
          width: Math.max(p.x - b.x, 0.005), height: Math.max(p.y - b.y, 0.005) } }));
      }
    };
    const onUp = async () => {
      const op = opRef.current;
      if (!op) return;
      opRef.current = null;
      if (op.type === "draw") {
        setDraft(d => {
          if (d && d.width > 0.004 && d.height > 0.004) {
            openPanelForNew(d);
          }
          return null;
        });
        setMode("select");
      } else if (op.type === "move" || op.type === "resize") {
        setTempBoxes(tb => {
          const bbox = tb[op.fieldId];
          if (bbox) {
            api.put(`/itp-templates/${itp.id}/mapped-fields/${op.fieldId}`, { bounding_box: bbox })
              .then(() => setFields(fs => fs.map(f => f.id === op.fieldId ? { ...f, bounding_box: bbox } : f)))
              .catch(() => toast.error("Could not save position"))
              .finally(() => setTempBoxes(t2 => { const c = { ...t2 }; delete c[op.fieldId]; return c; }));
          }
          return tb;
        });
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itp.id]);

  // ---------- panel ----------
  const openPanelForNew = (bbox) => {
    const t = drawTypeRef.current;
    setPanel({
      kind: "new",
      bbox,
      form: { field_type: t, label: "", required: false, itp_item_id: "" },
      itemForm: { ...EMPTY_ITEM },
      linkExisting: t !== "inspection_item",
    });
  };

  const openPanelForEdit = (field) => {
    const linked = field.itp_item_id ? itemById[field.itp_item_id] : null;
    setPanel({
      kind: "edit",
      field,
      form: {
        field_type: field.field_type, label: field.label || "",
        required: !!field.required, itp_item_id: field.itp_item_id || "",
      },
      itemForm: linked ? {
        item_number: linked.item_number || "", item_description: linked.item_description || "",
        requirement: linked.requirement || "", section_name: linked.section_name || "Mapped Items",
        point_type: "KEEP", evidence_required: linked.evidence_required || "",
        mandatory: linked.mandatory !== false,
        requires_contractor_signature: !!linked.requires_contractor_signature,
        requires_client_signature: !!linked.requires_client_signature,
        requires_client_release: !!linked.requires_client_release,
      } : { ...EMPTY_ITEM },
      linkExisting: true,
    });
  };

  const savePanel = async () => {
    if (!panel) return;
    setSaving(true);
    try {
      const { form, itemForm } = panel;
      if (panel.kind === "new") {
        const body = {
          field_type: form.field_type,
          page_number: pageNum,
          bounding_box: panel.bbox,
          label: form.label,
          required: form.required,
        };
        if (form.field_type === "inspection_item" && !panel.linkExisting) {
          if (!itemForm.item_description.trim()) { toast.error("Enter the inspection description"); setSaving(false); return; }
          body.item = { ...itemForm, point_type: itemForm.point_type === "NONE" ? "" : itemForm.point_type };
        } else if (form.itp_item_id) {
          body.itp_item_id = form.itp_item_id;
        }
        await api.post(`/itp-templates/${itp.id}/mapped-fields`, body);
        toast.success("Field mapped.");
      } else {
        const body = {
          field_type: form.field_type, label: form.label, required: form.required,
          itp_item_id: form.itp_item_id || "",
        };
        if (panel.field.itp_item_id && itemForm.item_description) {
          const { point_type, section_name, ...rest } = itemForm;
          body.item = rest;
        }
        await api.put(`/itp-templates/${itp.id}/mapped-fields/${panel.field.id}`, body);
        toast.success("Field updated.");
      }
      setPanel(null);
      await reloadFields();
      onTemplateChanged && onTemplateChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteField = async () => {
    if (!panel?.field) return;
    try {
      await api.delete(`/itp-templates/${itp.id}/mapped-fields/${panel.field.id}`);
      toast.success("Mapping deleted.");
      setPanel(null);
      await reloadFields();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const duplicateField = async () => {
    if (!panel?.field) return;
    const f = panel.field;
    const b = f.bounding_box;
    try {
      await api.post(`/itp-templates/${itp.id}/mapped-fields`, {
        field_type: f.field_type, page_number: f.page_number,
        bounding_box: { ...b, y: Math.min(b.y + b.height + 0.01, 1 - b.height) },
        label: f.label, required: f.required, itp_item_id: f.itp_item_id || undefined,
      });
      toast.success("Mapping duplicated.");
      setPanel(null);
      await reloadFields();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Duplicate failed");
    }
  };

  const setForm = (k, v) => setPanel(p => ({ ...p, form: { ...p.form, [k]: v } }));
  const setItemForm = (k, v) => setPanel(p => ({ ...p, itemForm: { ...p.itemForm, [k]: v } }));

  return (
    <div className="mt-3" data-testid="mapping-studio">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-t-md border border-slate-200 bg-slate-50 p-2">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" disabled={pageNum <= 1} onClick={() => setPageNum(p => p - 1)} data-testid="mapping-prev-page"><ChevronLeft className="size-4"/></Button>
          <span className="min-w-16 text-center font-mono text-xs" data-testid="mapping-page-indicator">{pageNum} / {numPages || "…"}</span>
          <Button variant="ghost" size="sm" disabled={pageNum >= numPages} onClick={() => setPageNum(p => p + 1)} data-testid="mapping-next-page"><ChevronRight className="size-4"/></Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} data-testid="mapping-zoom-out"><ZoomOut className="size-4"/></Button>
          <span className="min-w-12 text-center font-mono text-xs">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(4, +(z + 0.25).toFixed(2)))} data-testid="mapping-zoom-in"><ZoomIn className="size-4"/></Button>
          <Button variant="ghost" size="sm" onClick={() => setZoom(1)} title="Fit to width" data-testid="mapping-fit-width"><Maximize className="size-4"/></Button>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant={mode === "draw" && drawType === "inspection_item" ? "default" : "outline"}
            className={mode === "draw" && drawType === "inspection_item" ? "bg-teal-700 hover:bg-teal-800" : "border-teal-700 text-teal-700 hover:bg-teal-50"}
            onClick={() => { setDrawType("inspection_item"); setMode("draw"); }}
            data-testid="add-itp-item-btn"
          >
            <Plus className="mr-1.5 size-4"/> Add ITP Item
          </Button>
          <div className="flex items-center gap-1">
            <Select value={drawType === "inspection_item" ? "contractor_signature" : drawType} onValueChange={(v) => { setDrawType(v); setMode("draw"); }}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid="field-type-select">
                <PenLine className="mr-1 size-3.5"/><SelectValue placeholder="Add field…" />
              </SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.filter(([v]) => v !== "inspection_item").map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {mode === "draw" && (
            <Button size="sm" variant="ghost" onClick={() => setMode("select")} data-testid="mapping-cancel-draw">
              <MousePointer2 className="mr-1 size-3.5"/> Cancel
            </Button>
          )}
        </div>
      </div>
      {mode === "draw" && (
        <div className="border-x border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="draw-mode-hint">
          Click and drag over the document to map <b>{TYPE_LABEL[drawType]}</b>.
        </div>
      )}

      {/* Viewer */}
      <div ref={containerRef} className="max-h-[70vh] overflow-auto rounded-b-md border border-t-0 border-slate-200 bg-slate-100" data-testid="mapping-viewer">
        {!pageImg && (
          <div className="flex h-64 items-center justify-center text-sm text-slate-500">
            <Loader2 className="mr-2 size-4 animate-spin"/> Rendering document…
          </div>
        )}
        {pageImg && (
          <div
            ref={wrapperRef}
            className="relative mx-auto my-3 shadow"
            style={{ width: pageImg.w, height: pageImg.h, cursor: mode === "draw" ? "crosshair" : "default" }}
            onMouseDown={onWrapperMouseDown}
          >
            <img src={pageImg.url} alt={`Page ${pageNum}`} className="pointer-events-none absolute inset-0 size-full select-none" draggable={false}/>
            {rendering && <div className="absolute right-2 top-2"><Loader2 className="size-4 animate-spin text-teal-700"/></div>}

            {pageFields.map(f => {
              const b = tempBoxes[f.id] || f.bounding_box;
              const c = typeColor(f.field_type);
              const linked = f.itp_item_id ? itemById[f.itp_item_id] : null;
              return (
                <div
                  key={f.id}
                  className="group absolute"
                  style={{
                    left: `${b.x * 100}%`, top: `${b.y * 100}%`,
                    width: `${b.width * 100}%`, height: `${b.height * 100}%`,
                    border: `1.5px solid ${c.border}`, background: c.bg,
                    cursor: mode === "select" ? "move" : "inherit", borderRadius: 3,
                  }}
                  data-testid={`mapping-overlay-${f.id}`}
                  onMouseDown={(e) => {
                    if (mode !== "select" || e.button !== 0) return;
                    e.stopPropagation(); e.preventDefault();
                    opRef.current = { type: "move", fieldId: f.id, start: normFromEvent(e), orig: f.bounding_box };
                  }}
                  onDoubleClick={(e) => { e.stopPropagation(); openPanelForEdit(f); }}
                >
                  <button
                    className="absolute -top-5 left-0 whitespace-nowrap rounded px-1 py-px font-mono text-[10px] font-bold text-white opacity-90"
                    style={{ background: c.chip }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); openPanelForEdit(f); }}
                    title={linked ? linked.item_description : TYPE_LABEL[f.field_type]}
                    data-testid={`mapping-chip-${f.id}`}
                  >
                    {TYPE_ABBR[f.field_type]}{linked?.linked_hold_point ? ` · ${linked.linked_hold_point}` : ""}{linked?.item_number ? ` · ${linked.item_number}` : ""}
                  </button>
                  <div
                    className="absolute -bottom-1 -right-1 size-3 rounded-sm border border-white opacity-0 group-hover:opacity-100"
                    style={{ background: c.border, cursor: "nwse-resize" }}
                    onMouseDown={(e) => {
                      e.stopPropagation(); e.preventDefault();
                      opRef.current = { type: "resize", fieldId: f.id, orig: tempBoxes[f.id] || f.bounding_box };
                    }}
                  />
                </div>
              );
            })}

            {draft && (
              <div className="absolute border-2 border-dashed border-teal-600 bg-teal-500/10"
                style={{ left: `${draft.x * 100}%`, top: `${draft.y * 100}%`, width: `${draft.width * 100}%`, height: `${draft.height * 100}%` }}/>
            )}
          </div>
        )}
      </div>
      <div className="mt-1.5 text-xs text-slate-500" data-testid="mapping-summary">
        {fields.length} mapped field{fields.length === 1 ? "" : "s"} across the document · {pageFields.length} on this page ·
        double-click or use the chip to edit a mapping.
      </div>

      {/* Side panel */}
      <Sheet open={!!panel} onOpenChange={(o) => { if (!o) setPanel(null); }}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md" data-testid="mapping-panel">
          {panel && (
            <>
              <SheetHeader>
                <SheetTitle className="font-heading">
                  {panel.kind === "new" ? "Map Field" : "Edit Mapping"}
                </SheetTitle>
                <SheetDescription>
                  Page {pageNum} · position and details for this mapped area.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <Label>Field Type</Label>
                  <Select value={panel.form.field_type} onValueChange={(v) => setForm("field_type", v)}>
                    <SelectTrigger className="mt-1" data-testid="panel-field-type"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Label <span className="text-slate-400">(optional)</span></Label>
                  <Input className="mt-1" value={panel.form.label} onChange={(e) => setForm("label", e.target.value)} data-testid="panel-label"/>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={panel.form.required} onCheckedChange={(v) => setForm("required", !!v)} data-testid="panel-required"/>
                  Required field
                </label>

                {panel.form.field_type === "inspection_item" && panel.kind === "new" && (
                  <div className="flex gap-2">
                    <Button size="sm" variant={panel.linkExisting ? "outline" : "default"}
                      className={!panel.linkExisting ? "bg-teal-700 hover:bg-teal-800" : ""}
                      onClick={() => setPanel(p => ({ ...p, linkExisting: false }))} data-testid="panel-create-new-item">
                      Create new item
                    </Button>
                    <Button size="sm" variant={panel.linkExisting ? "default" : "outline"}
                      className={panel.linkExisting ? "bg-teal-700 hover:bg-teal-800" : ""}
                      onClick={() => setPanel(p => ({ ...p, linkExisting: true }))} data-testid="panel-link-existing-item">
                      Link existing item
                    </Button>
                  </div>
                )}

                {(panel.form.field_type !== "inspection_item" || panel.linkExisting) && (
                  <div>
                    <Label>Linked ITP Item {panel.form.field_type === "inspection_item" ? "" : "(recommended for signatures)"}</Label>
                    <Select value={panel.form.itp_item_id || "none"} onValueChange={(v) => setForm("itp_item_id", v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-1" data-testid="panel-linked-item"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {items.map(i => (
                          <SelectItem key={i.id} value={i.id}>
                            {(i.linked_hold_point ? `[${i.linked_hold_point}] ` : "") + (i.item_number ? `${i.item_number} · ` : "") + (i.item_description || "").slice(0, 60)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {panel.form.field_type === "inspection_item" && (!panel.linkExisting || (panel.kind === "edit" && panel.field?.itp_item_id)) && (
                  <div className="space-y-3 rounded-md border border-slate-200 p-3">
                    <div className="overline text-teal-700">{panel.kind === "new" ? "New Inspection Item" : "Inspection Item Details"}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Item Number</Label>
                        <Input className="mt-1" value={panel.itemForm.item_number} onChange={(e) => setItemForm("item_number", e.target.value)} data-testid="panel-item-number"/>
                      </div>
                      {panel.kind === "new" && (
                        <div>
                          <Label>Point Type</Label>
                          <Select value={panel.itemForm.point_type} onValueChange={(v) => setItemForm("point_type", v)}>
                            <SelectTrigger className="mt-1" data-testid="panel-point-type"><SelectValue/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="NONE">Standard</SelectItem>
                              <SelectItem value="HP">Hold Point (HP)</SelectItem>
                              <SelectItem value="WP">Witness Point (WP)</SelectItem>
                              <SelectItem value="AP">Approval Point (AP)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>Activity / Inspection Description</Label>
                      <Textarea className="mt-1" rows={2} value={panel.itemForm.item_description} onChange={(e) => setItemForm("item_description", e.target.value)} data-testid="panel-item-description"/>
                    </div>
                    <div>
                      <Label>Acceptance Criteria</Label>
                      <Textarea className="mt-1" rows={2} value={panel.itemForm.requirement} onChange={(e) => setItemForm("requirement", e.target.value)} data-testid="panel-item-criteria"/>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Evidence Required</Label>
                        <Input className="mt-1" value={panel.itemForm.evidence_required} onChange={(e) => setItemForm("evidence_required", e.target.value)} data-testid="panel-item-evidence"/>
                      </div>
                      {panel.kind === "new" && (
                        <div>
                          <Label>Section</Label>
                          <Input className="mt-1" value={panel.itemForm.section_name} onChange={(e) => setItemForm("section_name", e.target.value)} data-testid="panel-item-section"/>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <label className="flex items-center gap-2">
                        <Checkbox checked={panel.itemForm.mandatory} onCheckedChange={(v) => setItemForm("mandatory", !!v)}/> Mandatory for close-out
                      </label>
                      <label className="flex items-center gap-2">
                        <Checkbox checked={panel.itemForm.requires_contractor_signature} onCheckedChange={(v) => setItemForm("requires_contractor_signature", !!v)} data-testid="panel-req-contractor-sig"/> Contractor signature required
                      </label>
                      <label className="flex items-center gap-2">
                        <Checkbox checked={panel.itemForm.requires_client_signature} onCheckedChange={(v) => setItemForm("requires_client_signature", !!v)} data-testid="panel-req-client-sig"/> Client signature required
                      </label>
                      <label className="flex items-center gap-2">
                        <Checkbox checked={panel.itemForm.requires_client_release} onCheckedChange={(v) => setItemForm("requires_client_release", !!v)}/> Client release required
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-2">
                  <Button onClick={savePanel} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="mapping-save-btn">
                    {saving ? <Loader2 className="mr-2 size-4 animate-spin"/> : null} Save
                  </Button>
                  {panel.kind === "edit" && (
                    <>
                      <Button variant="outline" onClick={duplicateField} data-testid="mapping-duplicate-btn">
                        <Copy className="mr-1.5 size-4"/> Duplicate
                      </Button>
                      <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={deleteField} data-testid="mapping-delete-btn">
                        <Trash2 className="mr-1.5 size-4"/> Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
